import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import MuiPhoneNumber from 'material-ui-phone-number';
import createContact, { updateContact } from '../../../services/contact';
import { updateCompany } from '../../../services/company';
import User, { Company, Contact, CompanySubmissionData, ContactSubmissionData } from '../../../interfaces/users.interface';
import MultipleSelectChip from '../../Vendors/Forms/MultiSelect';
import { Service } from '../../../interfaces/vendor.interface';
import { StandardDialogActions } from '../../Modals/StandardDialog';
import CountrySelect from '../../Companies/CountrySelect';
import StateSelect from '../../Companies/StateSelect';
import CitySelect from '../../Companies/CitySelect';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';

const LocationForm = (props: { successCallback: any, submitButtonText: string, company: Company, location: (undefined | Contact), isPrimaryContact: boolean, modalClose?: any }) => {
  const {
    successCallback,
    submitButtonText,
    company,
    location,
    isPrimaryContact,
  } = props;

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    clearErrors,
    control,
  } = useForm();
  const [services, setServices] = useState<Service[]>(company.services ? company.services : []);
  const [selectedServices, setSelectedServices] = useState<Array<number>>([]);
  const [selectedSitePrimaryContact, setSelectedSitePrimaryContact] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const selectedCountry = watch('country');
  const selectedState = watch('state');
  const [countryCode, setCountryCode] = useState<number | undefined>(undefined);
  const handleCountyCodeChange = useCallback((code: number | undefined) => {
    setCountryCode(code);
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (!location) return;
    setValue('name', location.name);
    setValue('phoneNumber', location.phoneNumber);
    setValue('address', location.address);
    setValue('zipcode', location.zipcode);
    setValue('isPrimary', location.isPrimary);
    setValue('country', location.country);
    setValue('city', location.city);
    setValue('state', location.state);
    if (location.services) setSelectedServices(location.services.map(service => service.id));
    if (location.primaryContact) setSelectedSitePrimaryContact(location.primaryContact.id);
  }, []);

  useEffect(() => {
    setServices(company.services ? company.services : []);
  }, [company.services]);

  const update = async (contact: ContactSubmissionData): Promise<void> => {
    try {
      const responseContact = await updateContact(contact);
      clearErrors();

      if (isPrimaryContact) {
        const updatedCompany: Company = {
          ...company,
          contact: responseContact,
        };
        successCallback(updatedCompany);
        return;
      }

      const updatedCompanyData: CompanySubmissionData = {
        ...company,
        primaryContactUser: company.primaryContactUser?.id,
        employees: company.employees.map((employee) => employee.id),
        pendingEmployees: company.pendingEmployees.map((employee) => employee.id),
        services: company.services.map((service) => service.id),
        locations: [...company.locations.map((contactLocation: Contact) => contactLocation.id), responseContact.id],
        admins: company.admins.map(item => item.id),
        subsidiaries: company.subsidiaries.map(item => item.id),
      };
      const companyResponse = await updateCompany(updatedCompanyData);
      successCallback(companyResponse);
    } catch (responseError: any) {
      const errorData = responseError.response?.data;
      clearErrors();
      if (!errorData) return;
      for (const field of Object.keys(errorData)) {
        const errorMsgArr: string[] = errorData[field];
        setError(field, { type: 'manual', message: errorMsgArr[0] });
      }
    }
  };

  const create = async (contact: ContactSubmissionData): Promise<void> => {
    try {
      const responseContact = await createContact(contact);
      clearErrors();
      reset();

      const updatedCompany: CompanySubmissionData = {
        ...company,
        primaryContactUser: company.primaryContactUser?.id,
        employees: company.employees.map((employee) => employee.id),
        pendingEmployees: company.pendingEmployees.map((employee) => employee.id),
        services: company.services.map((service) => service.id),
        locations: [...company.locations.map((contactLocation: Contact) => contactLocation.id), responseContact.id],
        admins: company.admins.map(item => item.id),
        subsidiaries: company.subsidiaries.map(item => item.id),
      };
      const companyResponse = await updateCompany(updatedCompany);
      successCallback(companyResponse);
    } catch (responseError: any) {
      const errorData = responseError.response?.data;
      clearErrors();
      if (!errorData) return;
      for (const field of Object.keys(errorData)) {
        const errorMsgArr: string[] = errorData[field];
        setError(field, { type: 'manual', message: errorMsgArr[0] });
      }
    }
  };

  const onSubmit = async (data: FieldValues) => {
    if (selectedServices.length === 0) {
      setErrorMessage('You must select at least 1 service.');
      return;
    }
    setIsSubmitting(true);
    setErrorMessage('');
    const newContact: ContactSubmissionData = {
      name: data.name,
      phoneNumber: data.phoneNumber,
      address: data.address,
      country: data.country,
      state: data.state,
      city: data.city,
      zipcode: data.zipcode,
      isPrimary: false,
      primaryContact: selectedSitePrimaryContact ? selectedSitePrimaryContact : undefined,
      services: selectedServices,
    };
    try {
      if (location) {
        newContact.id = location.id;
        await update(newContact);
      } else {
        await create(newContact);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid container>
      <Box sx={{ width: '100%' }} component="form" method="post" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              margin='normal'
              id='name'
              label='Location Name'
              autoFocus
              {...register('name')}
              error={!!errors.name}
              helperText={typeof errors.name?.message === 'string' ? errors.name?.message : undefined}
              autoComplete="do-not-autofill"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              margin='normal'
              id='address'
              label='Address'
              autoFocus
              {...register('address')}
              autoComplete="do-not-autofill"
            />
          </Grid>
          <CountrySelect
            id={'country-select'}
            registerValue={'country'}
            originalCountry={location?.country}
            register={register} setValue={setValue}
            handleCountyCodeChange={handleCountyCodeChange}
          />
          <StateSelect
            id={'state'}
            registerValue={'state'}
            originalState={location?.state}
            register={register}
            setValue={setValue}
            selectedCountry={selectedCountry}
          />
           <CitySelect
            id={'city'}
            registerValue={'city'}
            originalCity={location?.city}
            register={register}
            setValue={setValue}
            selectedState={selectedState}
            countryCode={countryCode}
          />
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              margin='normal'
              id='zipcode'
              label='Postal Code'
              autoFocus
              {...register('zipcode')}
              autoComplete="do-not-autofill"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              render={({ field }) => (<MuiPhoneNumber
                value={field.value}
                required
                fullWidth
                margin='normal'
                id='phoneNumber'
                label='Phone Number'
                InputLabelProps={{ shrink: true }}
                defaultCountry={'us'}
                variant='outlined'
                onChange={field.onChange}
              />)}
              name={'phoneNumber'}
              control={control}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="location-primary-contact">Primary Contact</InputLabel>
              <Select
                labelId="location-primary-contact"
                id="location-primary-contact-select"
                label="Primary Contact"
                defaultValue={location?.primaryContact ? location.primaryContact.id : ''}
                onChange={(event: any, selectedEmployee: any | null) => {
                  event.preventDefault();
                  setSelectedSitePrimaryContact(selectedEmployee.props.value);
                }}
              >
                {company.employees.map((employee: User) => <MenuItem key={employee.id} value={employee.id} selected={location?.primaryContact?.id == employee.id}>{employee.email}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <MultipleSelectChip options={services} optionsCallback={setSelectedServices} selectedOptions={selectedServices} inputLabel='Services' />
            {errorMessage.length > 0 && <Alert sx={{ mt: 1 }} severity="error">{errorMessage}</Alert>}
          </Grid>
        </Grid>
        <StandardDialogActions>
          {props.modalClose && <Button sx={{ mr: 1 }} variant="outlined" onClick={() => props.modalClose()}>Close</Button>}
          <AsyncSaveButton
            type="submit"
            variant="contained"
            isSaving={isSubmitting}
            endIconWhenIdle={<NavigateNextIcon />}
            label={submitButtonText}
            savingLabel="Saving..."
          />
        </StandardDialogActions>
      </Box>
    </Grid>
  );
};

LocationForm.defaultProps = {
  location: undefined,
  isPrimaryContact: false,
};

export default LocationForm;